import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Icon from '@mdi/react';
import {
  mdiClose,
  mdiSend,
  mdiPaperclip,
  mdiLoading,
  mdiCheck,
  mdiUndoVariant,
  mdiAlertCircleOutline,
  mdiFilePdfBox,
  mdiDeleteSweepOutline,
} from '@mdi/js';
import { CircuitSchema } from '../types';
import {
  apiService,
  AgentAction,
  AgentAttachment,
  AgentStatus,
} from '../services/apiService';
import { diffSchemas, isEmptyDiff, SchemaDiff } from '../services/schemaDiff';
import { OptimizationReportCard } from './OptimizationReportCard';

/* ────────────────────────── Types ────────────────────────── */

type ProposalState = 'pending' | 'applied' | 'discarded';

interface Proposal {
  schema: CircuitSchema;
  /** Canvas the assistant worked from, to warn if it changed meanwhile. */
  baseKey: string;
  diff: SchemaDiff;
  simulated: boolean;
  state: ProposalState;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  attachments?: string[];
  actions?: AgentAction[];
  proposal?: Proposal;
  error?: boolean;
  /** Models that answered this turn (shown when OpenRouter picks the model). */
  modelsUsed?: string[];
}

interface AgentPanelProps {
  schema: CircuitSchema;
  status: AgentStatus | null;
  isLoggedIn: boolean;
  onApply: (schema: CircuitSchema, opts: { simulate: boolean }) => void;
  onClose: () => void;
  onRequestLogin?: () => void;
}

/* ────────────────────────── Helpers ────────────────────────── */

const MAX_IMAGE_EDGE = 1600;
const MAX_FILE_BYTES = 8 * 1024 * 1024;
const MAX_ATTACHMENTS = 4;

const SUGGESTIONS = [
  'Build a BEV battery loop: 8 kW battery, 3 kW motor, 1.5 kW inverter and a BSPL cooling package.',
  'Add a hot-day scenario at 45 °C with the battery at 12 kW.',
  'Check this circuit and recommend a cooling package.',
  'Find the best arrangement of the heat sources and the lowest pump flow that keeps every temperature limit.',
];

const TOOL_LABELS: Record<string, string> = {
  get_circuit: 'Read the circuit',
  search_library: 'Searched the library',
  list_fluids: 'Listed coolants',
  add_component: 'Added component',
  update_component: 'Changed component',
  remove_component: 'Removed component',
  connect: 'Connected',
  disconnect: 'Disconnected',
  clear_circuit: 'Cleared the canvas',
  set_fluid: 'Set coolant',
  add_scenario: 'Added scenario',
  update_scenario: 'Changed scenario',
  remove_scenario: 'Removed scenario',
  validate_circuit: 'Validated',
  run_simulation: 'Ran simulation',
  recommend_cooling_package: 'Ranked cooling packages',
  optimize_design: 'Optimised the design',
  compare_topologies: 'Compared layouts',
};

/** Structural fingerprint of a canvas (ignores positions and selection). */
export function canvasKey(schema: CircuitSchema): string {
  return JSON.stringify({
    nodes: (schema.nodes || []).map(n => [n.id, n.data?.component?.id, n.data?.custom_name, n.data?.properties]),
    edges: (schema.edges || []).map(e => [e.source, e.sourceHandle, e.target, e.targetHandle]),
    params: schema.simulation_params,
  });
}

function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Photos are downscaled to keep requests small; the model needs no more. */
async function toAttachment(file: File): Promise<AgentAttachment> {
  if (file.type === 'application/pdf') {
    if (file.size > MAX_FILE_BYTES) throw new Error(`${file.name} is larger than 8 MB.`);
    return { name: file.name, data: await readAsDataUrl(file) };
  }
  if (!file.type.startsWith('image/')) {
    throw new Error(`${file.name}: only images and PDFs can be attached.`);
  }
  const original = await readAsDataUrl(file);
  if (file.type === 'image/gif') return { name: file.name, data: original };
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = original;
    });
    const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return { name: file.name, data: original };
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return { name: file.name, data: canvas.toDataURL('image/jpeg', 0.85) };
  } catch {
    return { name: file.name, data: original };
  }
}

function describeDiff(d: SchemaDiff): string[] {
  const lines: string[] = [];
  if (d.addedNodes.length) lines.push(`+ ${d.addedNodes.length} component${d.addedNodes.length > 1 ? 's' : ''}: ${d.addedNodes.join(', ')}`);
  if (d.removedNodes.length) lines.push(`− ${d.removedNodes.length} removed: ${d.removedNodes.join(', ')}`);
  if (d.changedNodes.length) lines.push(`~ changed: ${d.changedNodes.join(', ')}`);
  if (d.addedEdges || d.removedEdges) {
    lines.push(`connections: +${d.addedEdges} / −${d.removedEdges}`);
  }
  if (d.scenariosChanged) {
    lines.push(d.scenariosBefore === d.scenariosAfter
      ? `scenarios edited (${d.scenariosAfter})`
      : `scenarios: ${d.scenariosBefore} → ${d.scenariosAfter}`);
  }
  if (d.fluidChanged) lines.push('coolant changed');
  return lines;
}

/* ────────────────────────── Styles ────────────────────────── */

const Panel = styled.div<{ $visible: boolean }>`
  width: 360px;
  flex-shrink: 0;
  background: #ffffff;
  border-left: 1px solid #e8eaed;
  display: ${p => (p.$visible ? 'flex' : 'none')};
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #e8eaed;
  font-weight: 600;
  font-size: 13px;
  color: #202124;
`;

const ProgressList = styled.ul`
  margin: 4px 0 0;
  padding-left: 16px;
  font-size: 11px;
  color: #9aa0a6;
`;

const ModelNote = styled.div`
  margin-top: 4px;
  font-size: 10px;
  color: #9aa0a6;
`;

const HeaderSub = styled.span`
  font-weight: 400;
  font-size: 11px;
  color: #9aa0a6;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IconBtn = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: #5f6368;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  &:hover { background: #f1f3f4; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const Messages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Bubble = styled.div<{ $role: 'user' | 'assistant'; $error?: boolean }>`
  align-self: ${p => (p.$role === 'user' ? 'flex-end' : 'stretch')};
  max-width: ${p => (p.$role === 'user' ? '85%' : '100%')};
  background: ${p => (p.$error ? '#fce8e6' : p.$role === 'user' ? '#e8f0fe' : '#f8f9fa')};
  color: ${p => (p.$error ? '#c5221f' : '#202124')};
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12.5px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: #ffffff;
  border: 1px solid #dadce0;
  border-radius: 10px;
  padding: 1px 7px;
  font-size: 10.5px;
  color: #5f6368;
`;

const Steps = styled.details`
  margin-top: 6px;
  font-size: 11px;
  color: #5f6368;
  summary { cursor: pointer; }
  li { margin: 1px 0; }
`;

const ProposalCard = styled.div<{ $state: ProposalState }>`
  margin-top: 8px;
  border: 1px solid ${p => (p.$state === 'pending' ? '#1a73e8' : '#dadce0')};
  border-radius: 6px;
  padding: 8px;
  background: #ffffff;
  font-size: 11.5px;
  color: #3c4043;
  ul { margin: 4px 0 8px; padding-left: 16px; }
`;

const ProposalActions = styled.div`
  display: flex;
  gap: 6px;
`;

const Btn = styled.button<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  border: 1px solid ${p => (p.$primary ? '#1a73e8' : '#dadce0')};
  background: ${p => (p.$primary ? '#1a73e8' : '#ffffff')};
  color: ${p => (p.$primary ? '#ffffff' : '#3c4043')};
  &:disabled { opacity: 0.5; cursor: default; }
`;

const Suggestion = styled.button`
  text-align: left;
  border: 1px solid #dadce0;
  background: #ffffff;
  border-radius: 6px;
  padding: 7px 9px;
  font-size: 12px;
  font-family: inherit;
  color: #3c4043;
  cursor: pointer;
  &:hover { border-color: #1a73e8; background: #f8fbff; }
`;

const Intro = styled.div`
  font-size: 12px;
  color: #5f6368;
  line-height: 1.5;
`;

const Composer = styled.div`
  border-top: 1px solid #e8eaed;
  padding: 8px 10px 10px;
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  resize: none;
  border: 1px solid #dadce0;
  border-radius: 6px;
  padding: 7px 9px;
  font-size: 12.5px;
  font-family: inherit;
  min-height: 58px;
  max-height: 160px;
  &:focus { outline: none; border-color: #1a73e8; }
`;

const ComposerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
`;

const Spin = styled(Icon)`
  animation: agent-spin 1s linear infinite;
  @keyframes agent-spin { to { transform: rotate(360deg); } }
`;

const Notice = styled.div`
  padding: 16px 12px;
  font-size: 12px;
  color: #5f6368;
  line-height: 1.5;
`;

/* ────────────────────────── Component ────────────────────────── */

export const AgentPanel: React.FC<AgentPanelProps & { visible: boolean }> = ({
  schema,
  status,
  isLoggedIn,
  onApply,
  onClose,
  onRequestLogin,
  visible,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState<AgentAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<{ steps: string[]; elapsed: number }>({ steps: [], elapsed: 0 });
  const fileRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const schemaRef = useRef(schema);
  schemaRef.current = schema;

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages, loading]);

  const addFiles = useCallback(async (files: FileList | File[]) => {
    const list = Array.from(files).slice(0, MAX_ATTACHMENTS);
    const errors: string[] = [];
    const added: AgentAttachment[] = [];
    for (const file of list) {
      try {
        added.push(await toAttachment(file));
      } catch (e: any) {
        errors.push(e.message || String(e));
      }
    }
    setAttachments(prev => [...prev, ...added].slice(0, MAX_ATTACHMENTS));
    if (errors.length) {
      setMessages(prev => [...prev, { role: 'assistant', content: errors.join('\n'), error: true }]);
    }
  }, []);

  const send = useCallback(async (text?: string) => {
    const message = (text ?? input).trim();
    if ((!message && attachments.length === 0) || loading) return;

    const base = schemaRef.current;
    const history = messages
      .filter(m => !m.error && m.content)
      .map(m => ({ role: m.role, content: m.content }));
    const sentAttachments = attachments;

    setMessages(prev => [...prev, {
      role: 'user',
      content: message || '(attachment)',
      attachments: sentAttachments.map(a => a.name),
    }]);
    setInput('');
    setAttachments([]);
    setLoading(true);
    setProgress({ steps: [], elapsed: 0 });

    try {
      const res = await apiService.agentChat({
        message,
        schema: base,
        history,
        attachments: sentAttachments.length ? sentAttachments : undefined,
      }, (steps, elapsed) => setProgress({ steps, elapsed }));
      let proposal: Proposal | undefined;
      if (res.changed && res.schema) {
        const diff = diffSchemas(base, res.schema);
        if (!isEmptyDiff(diff)) {
          proposal = {
            schema: res.schema,
            baseKey: canvasKey(base),
            diff,
            simulated: !!(res.simulation && res.simulation.length),
            state: 'pending',
          };
        }
      }
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.reply,
        actions: res.actions,
        modelsUsed: res.models_used,
        proposal,
      }]);
    } catch (e: any) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: e.message || 'The assistant failed.',
        error: true,
      }]);
    } finally {
      setLoading(false);
    }
  }, [input, attachments, loading, messages]);

  const setProposalState = (index: number, state: ProposalState) => {
    setMessages(prev => prev.map((m, i) =>
      i === index && m.proposal ? { ...m, proposal: { ...m.proposal, state } } : m
    ));
  };

  const apply = (index: number) => {
    const proposal = messages[index].proposal;
    if (!proposal) return;
    if (canvasKey(schemaRef.current) !== proposal.baseKey) {
      const ok = window.confirm(
        'The canvas changed after you asked the assistant. Applying its proposal replaces those edits. Continue?'
      );
      if (!ok) return;
    }
    onApply(proposal.schema, { simulate: proposal.simulated });
    // Older pending proposals were built on a canvas that no longer exists.
    setMessages(prev => prev.map((m, i) => {
      if (!m.proposal) return m;
      if (i === index) return { ...m, proposal: { ...m.proposal, state: 'applied' } };
      return m.proposal.state === 'pending' ? { ...m, proposal: { ...m.proposal, state: 'discarded' } } : m;
    }));
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(e.clipboardData.files || []);
    if (files.length) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const renderBody = () => {
    if (!status) return <Notice>Connecting to the assistant…</Notice>;
    if (!status.configured) {
      return (
        <Notice>
          The assistant is not configured on this server. An administrator needs to set
          <code> OPENROUTER_API_KEY</code> for the backend.
        </Notice>
      );
    }
    if (status.requires_login && !isLoggedIn) {
      return (
        <Notice>
          Sign in to use the assistant.{' '}
          {onRequestLogin && <Btn onClick={onRequestLogin}>Sign in</Btn>}
        </Notice>
      );
    }
    return (
      <>
        <Messages ref={listRef} data-testid="agent-messages">
          {messages.length === 0 && (
            <>
              <Intro>
                Describe the circuit you need, attach a sketch or a PDF schematic, or ask for
                scenarios and a cooling-package check. Changes are shown as a proposal you can
                apply or discard.
              </Intro>
              {SUGGESTIONS.map(s => (
                <Suggestion key={s} onClick={() => send(s)}>{s}</Suggestion>
              ))}
            </>
          )}
          {messages.map((m, i) => (
            <Bubble key={i} $role={m.role} $error={m.error}>
              {m.error && <Icon path={mdiAlertCircleOutline} size={0.5} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
              {m.content}
              {status?.auto_routing && m.modelsUsed && m.modelsUsed.length > 0 && (
                <ModelNote data-testid="agent-model-used">via {m.modelsUsed.join(', ')}</ModelNote>
              )}
              {m.attachments && m.attachments.length > 0 && (
                <Chips>{m.attachments.map(a => <Chip key={a}><Icon path={mdiPaperclip} size={0.4} />{a}</Chip>)}</Chips>
              )}
              {m.actions && m.actions.length > 0 && (
                <Steps>
                  <summary>{m.actions.length} step{m.actions.length > 1 ? 's' : ''}</summary>
                  <ul>
                    {m.actions.map((a, j) => (
                      <li key={j} style={{ color: a.ok ? undefined : '#c5221f' }}>
                        {TOOL_LABELS[a.tool] || a.tool}
                        {a.arguments?.name ? ` “${a.arguments.name}”` : ''}
                        {a.error ? ` — ${a.error}` : ''}
                      </li>
                    ))}
                  </ul>
                </Steps>
              )}
              {m.actions?.filter(a => a.report).map((a, j) => (
                <OptimizationReportCard key={`report-${j}`} report={a.report!} />
              ))}
              {m.proposal && (
                <ProposalCard $state={m.proposal.state} data-testid="agent-proposal">
                  <strong>
                    {m.proposal.state === 'applied' ? 'Applied to canvas'
                      : m.proposal.state === 'discarded' ? 'Discarded'
                      : 'Proposed changes'}
                  </strong>
                  <ul>{describeDiff(m.proposal.diff).map(line => <li key={line}>{line}</li>)}</ul>
                  {m.proposal.state === 'pending' && (
                    <ProposalActions>
                      <Btn $primary onClick={() => apply(i)}>
                        <Icon path={mdiCheck} size={0.5} /> Apply to canvas
                      </Btn>
                      <Btn onClick={() => setProposalState(i, 'discarded')}>
                        <Icon path={mdiUndoVariant} size={0.5} /> Discard
                      </Btn>
                    </ProposalActions>
                  )}
                </ProposalCard>
              )}
            </Bubble>
          ))}
          {loading && (
            <Bubble $role="assistant">
              <Spin path={mdiLoading} size={0.55} />{' '}
              {progress.steps[progress.steps.length - 1] || 'Working on it…'}
              {progress.elapsed >= 5 && <ModelNote>{Math.round(progress.elapsed)} s</ModelNote>}
              {progress.steps.length > 2 && (
                <ProgressList data-testid="agent-progress">
                  {progress.steps.slice(1, -1).slice(-6).map((step, i) => <li key={i}>{step}</li>)}
                </ProgressList>
              )}
            </Bubble>
          )}
        </Messages>

        <Composer>
          {attachments.length > 0 && (
            <Chips style={{ marginBottom: 6 }}>
              {attachments.map((a, i) => (
                <Chip key={`${a.name}-${i}`}>
                  <Icon path={a.data.startsWith('data:application/pdf') ? mdiFilePdfBox : mdiPaperclip} size={0.4} />
                  {a.name}
                  <IconBtn
                    style={{ padding: 0 }}
                    aria-label={`Remove ${a.name}`}
                    onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}
                  >
                    <Icon path={mdiClose} size={0.4} />
                  </IconBtn>
                </Chip>
              ))}
            </Chips>
          )}
          <TextArea
            placeholder="Ask the assistant… (Enter to send, Shift+Enter for a new line)"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            disabled={loading}
            aria-label="Message to the assistant"
          />
          <ComposerRow>
            <IconBtn
              title="Attach a sketch, photo or PDF schematic"
              onClick={() => fileRef.current?.click()}
              disabled={loading || attachments.length >= MAX_ATTACHMENTS}
            >
              <Icon path={mdiPaperclip} size={0.65} />
            </IconBtn>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              multiple
              style={{ display: 'none' }}
              onChange={e => {
                if (e.target.files) addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <div style={{ flex: 1 }} />
            <Btn
              $primary
              onClick={() => send()}
              disabled={loading || (!input.trim() && attachments.length === 0)}
            >
              <Icon path={mdiSend} size={0.5} /> Send
            </Btn>
          </ComposerRow>
        </Composer>
      </>
    );
  };

  return (
    <Panel $visible={visible} data-testid="agent-panel">
      <Header>
        Assistant
        <HeaderSub title={status?.model || ''}>
          {status?.auto_routing ? 'Auto model (OpenRouter)' : status?.model || ''}
        </HeaderSub>
        <IconBtn
          title="Start a new conversation"
          onClick={() => setMessages([])}
          disabled={loading || messages.length === 0}
        >
          <Icon path={mdiDeleteSweepOutline} size={0.6} />
        </IconBtn>
        <IconBtn title="Close" onClick={onClose}>
          <Icon path={mdiClose} size={0.6} />
        </IconBtn>
      </Header>
      {renderBody()}
    </Panel>
  );
};

export default AgentPanel;
